/**
 * Client-side rolling diagnostics buffer for the "Report a Problem" flow.
 *
 * Staff won't open dev tools (tablets, kiosk Windows machines) and won't type
 * much — so the last few console errors/warnings, uncaught errors, rejected
 * promises, and failed network calls are recorded in memory and attached
 * automatically to every problem report. This is the "copy-paste the console
 * for me" automation: it covers the gap between Axiom (which only sees thrown
 * exceptions) and the staff narrative (which rarely has technical detail).
 *
 * Deliberately tiny and defensive:
 *   - In-memory ring buffer only (nothing persisted, nothing sent until the
 *     staff member presses Send on a report).
 *   - Every hook is wrapped so a bug here can never break console/fetch.
 *   - Messages are truncated; the serialized snapshot is size-capped so the
 *     report POST stays small on facility wifi.
 */

export interface DiagnosticEntry {
  /** ISO timestamp of when the event was recorded. */
  at: string
  kind:
    | "console.error"
    | "console.warn"
    | "window.error"
    | "unhandledrejection"
    | "fetch.failed"
  /** Human-readable summary, truncated. */
  message: string
  /** Optional extra context (URL + status for fetch, stack head, …), truncated. */
  detail?: string
}

const MAX_ENTRIES = 25
const MAX_MESSAGE_CHARS = 500
const MAX_DETAIL_CHARS = 300
/** Serialized-snapshot budget — keeps the report POST + email + DB row small. */
export const MAX_SNAPSHOT_CHARS = 16_000

// Endpoints whose failures are reported through their own channels — recording
// them here would loop (report-problem) or double-count (telemetry).
const IGNORED_FETCH_PATHS = ["/api/report-problem", "/api/telemetry"]

let buffer: DiagnosticEntry[] = []
let initialized = false

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max - 1) + "…" : value
}

function stringifyArg(arg: unknown): string {
  if (typeof arg === "string") return arg
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`
  try {
    return JSON.stringify(arg)
  } catch {
    return String(arg)
  }
}

/** Record one diagnostic event. Safe to call anywhere; never throws. */
export function recordDiagnostic(
  kind: DiagnosticEntry["kind"],
  message: string,
  detail?: string,
): void {
  try {
    buffer.push({
      at: new Date().toISOString(),
      kind,
      message: truncate(message, MAX_MESSAGE_CHARS),
      ...(detail ? { detail: truncate(detail, MAX_DETAIL_CHARS) } : {}),
    })
    if (buffer.length > MAX_ENTRIES) buffer = buffer.slice(-MAX_ENTRIES)
  } catch {
    // Diagnostics must never crash the app.
  }
}

/**
 * Snapshot for a problem report: newest-last copy of the buffer, dropping the
 * oldest entries until the serialized form fits the size budget.
 */
export function getDiagnosticsSnapshot(): DiagnosticEntry[] {
  let snapshot = [...buffer]
  while (snapshot.length > 0 && JSON.stringify(snapshot).length > MAX_SNAPSHOT_CHARS) {
    snapshot = snapshot.slice(1)
  }
  return snapshot
}

/** Test-only: clear the buffer between cases. */
export function resetDiagnosticsBuffer(): void {
  buffer = []
  // `initialized` intentionally NOT reset — hooks stay patched once installed.
}

function summarizeDbCall(body: unknown): string | undefined {
  if (typeof body !== "string") return undefined
  try {
    const parsed = JSON.parse(body) as { action?: unknown; collectionId?: unknown }
    if (typeof parsed.action === "string" || typeof parsed.collectionId === "string") {
      return `${String(parsed.action ?? "?")} ${String(parsed.collectionId ?? "?")}`
    }
  } catch {
    /* non-JSON body — nothing useful to summarize */
  }
  return undefined
}

/**
 * Install the capture hooks (console, window error events, fetch). Idempotent —
 * call from any client component mount; only the first call patches anything.
 */
export function initDiagnosticsBuffer(): void {
  if (initialized || typeof window === "undefined") return
  initialized = true

  try {
    const originalError = console.error.bind(console)
    console.error = (...args: unknown[]) => {
      originalError(...args)
      recordDiagnostic("console.error", args.map(stringifyArg).join(" "))
    }
    const originalWarn = console.warn.bind(console)
    console.warn = (...args: unknown[]) => {
      originalWarn(...args)
      recordDiagnostic("console.warn", args.map(stringifyArg).join(" "))
    }
  } catch {
    /* console patch failed — keep going, other hooks still help */
  }

  try {
    window.addEventListener("error", (event) => {
      recordDiagnostic(
        "window.error",
        event.message || "Unknown script error",
        event.filename ? `${event.filename}:${event.lineno ?? 0}` : undefined,
      )
    })
    window.addEventListener("unhandledrejection", (event) => {
      recordDiagnostic("unhandledrejection", stringifyArg(event.reason))
    })
  } catch {
    /* listener install failed */
  }

  try {
    const originalFetch = window.fetch.bind(window)
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url =
        typeof input === "string" ? input : input instanceof URL ? input.href : input.url
      const ignored = IGNORED_FETCH_PATHS.some((p) => url.includes(p))
      try {
        const res = await originalFetch(input, init)
        if (!ignored && res.status >= 400) {
          const dbSummary = url.includes("/api/db") ? summarizeDbCall(init?.body) : undefined
          recordDiagnostic(
            "fetch.failed",
            `${init?.method ?? "GET"} ${url} → ${res.status}`,
            dbSummary,
          )
        }
        return res
      } catch (err) {
        if (!ignored) {
          recordDiagnostic(
            "fetch.failed",
            `${init?.method ?? "GET"} ${url} → network error`,
            stringifyArg(err),
          )
        }
        throw err
      }
    }
  } catch {
    /* fetch patch failed */
  }
}
